import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Момент, до которого нужно подтвердить email (Unix epoch). Считает бэкенд из
  // EMAIL_VERIFY_GRACE_HOURS — дублировать этот срок в интерфейсе нельзя, разойдётся.
  const [emailVerifyDeadline, setEmailVerifyDeadline] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      api.get('/auth/me')
        .then(res => {
          setUser(res.data.user);
          setEmailVerifyDeadline(res.data.email_verify_deadline ?? null);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const registerStart = async (data) => {
    const res = await api.post('/auth/register/start', data);
    return res.data; // { pending_id, resend_after, dev_otp? }
  };

  const registerVerify = async (pending_id, otp) => {
    const res = await api.post('/auth/register/verify', { pending_id, otp });
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    return res.data.user;
  };

  const registerResend = async (pending_id) => {
    const res = await api.post('/auth/register/resend', { pending_id });
    return res.data; // { resend_after, dev_otp? }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const refreshUser = async () => {
    const res = await api.get('/auth/me');
    setUser(res.data.user);
    setEmailVerifyDeadline(res.data.email_verify_deadline ?? null);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    return res.data.user;
  };

  // Подтверждение email по ссылке из письма — публичный вызов, токена в нём нет.
  const verifyEmail = async (token) => {
    const res = await api.post('/auth/verify-email', { token });
    return res.data;
  };

  // Повторная отправка письма: для залогиненного — по Bearer-токену, для аккаунта,
  // уже заблокированного за просрочку (войти он не может), — по email+паролю.
  const resendVerification = async () => {
    const res = await api.post('/auth/verify-email/resend');
    return res.data;
  };

  const requestVerification = async (email, password) => {
    const res = await api.post('/auth/verify-email/request', { email, password });
    return res.data;
  };

  return (
    <AuthContext.Provider value={{
      user, loading, emailVerifyDeadline, login, registerStart, registerVerify, registerResend,
      logout, refreshUser, verifyEmail, resendVerification, requestVerification,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
